import { CKButton, CKLayoutGridAuto } from 'clearkit';
import { useState } from 'react';

import { AuthLayoutHeader } from '~/components/layouts/AuthLayout';

import { AuthBox } from '../../AuthBox';
import TextFieldWithValidations from '../../shared/TextFieldWithValidations';
import useCreateAccountAndSignIn from '../../shared/useCreateAccountAndSignIn';
import useTelemetryId from '../../shared/useTelemetryId';
import { CreateAccountInput } from '../types';

type CreateAccountProps = {
  proposedOrg: CreateAccountInput;
};

type CreateAccountValidationErrors = {
  account_name?: string[];
  user_first_name?: string[];
  user_last_name?: string[];
};

export default function CreateAccount({ proposedOrg }: CreateAccountProps) {
  const [userFirstName, setUserFirstName] = useState(
    proposedOrg.user_first_name || '',
  );
  const [userLastName, setUserLastName] = useState(
    proposedOrg.user_last_name || '',
  );
  const [accountName, setAccountName] = useState(
    proposedOrg.organization_name || '',
  );
  const [validationErrors, setValidationErrors] = useState<
    CreateAccountValidationErrors
  >({});

  const isBlank =
    userFirstName === '' || userLastName === '' || accountName === '';

  const { isLoading, mutateAsync } = useCreateAccountAndSignIn();
  const { getTelemetryId } = useTelemetryId();

  async function createAccount() {
    const telemetryId = await getTelemetryId();

    const { isSuccess, error, redirect } = await mutateAsync({
      accountName,
      userFirstName,
      userLastName,
      telemetryId,
    });

    if (isSuccess) {
      setValidationErrors({});
      window.location.href = redirect;
      return;
    }

    if (
      error.action === 'unauthorized' ||
      error?.error.message_code === 'login_error'
    ) {
      window.location.href = `/auth/login?error=${error.type ||
        error?.error.message_code}`;
      return;
    }

    if (error.error?.type === 'validation') {
      setValidationErrors(error.error.errors);
      return;
    }
  }

  return (
    <>
      <AuthLayoutHeader
        description=""
        header={
          <div className="w-full font-normal text-center">
            Confirm the details below to finish setting up your Clearbit account
          </div>
        }
      />
      <AuthBox>
        <div className="flex flex-col gap-6">
          <CKLayoutGridAuto gap="1.5rem">
            <TextFieldWithValidations
              name="user_first_name"
              onChange={setUserFirstName}
              placeholder="Enter your first name"
              validationErrors={validationErrors}
              value={userFirstName}
            >
              <TextFieldWithValidations.Label>
                First name
              </TextFieldWithValidations.Label>
            </TextFieldWithValidations>

            <TextFieldWithValidations
              name="user_last_name"
              onChange={setUserLastName}
              placeholder="Enter your last name"
              validationErrors={validationErrors}
              value={userLastName}
            >
              <TextFieldWithValidations.Label>
                Last name
              </TextFieldWithValidations.Label>
            </TextFieldWithValidations>
          </CKLayoutGridAuto>
          <TextFieldWithValidations
            name="account_name"
            onChange={setAccountName}
            placeholder="Enter your company name"
            validationErrors={validationErrors}
            value={accountName}
          >
            <TextFieldWithValidations.Label>
              Company name
            </TextFieldWithValidations.Label>
          </TextFieldWithValidations>
          <div>
            <CKButton
              isDisabled={isBlank}
              isLoading={isLoading}
              onClick={createAccount}
              variant="bold"
              variantColor="blue"
            >
              Finish setting up my account
            </CKButton>
          </div>
        </div>
      </AuthBox>

      <div>
        <div className="text-sm text-center text-gray-600">
          <span>Need help?</span>
          <a
            className="pl-2 font-medium text-blue-600 hover:text-blue-500"
            href="mailto:support@clearbit.com"
          >
            Contact us at support@clearbit.com
          </a>
        </div>
      </div>
    </>
  );
}
